import type {
  DataTag,
  MutationFunction,
  QueryFunction,
  QueryKey,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
  UseSuspenseQueryOptions,
  UseSuspenseQueryResult,
} from '@tanstack/react-query';
import { keepPreviousData, useMutation, useQuery, useSuspenseQuery } from '@tanstack/react-query';
import type { BodyType, ErrorType } from '../../axios';
import { request } from '../../axios';
import type { AppointmentDto } from '../model/appointmentDto.ts';
import type { LabOrderDto } from '../model/labOrderDto.ts';
import type { TracingContactDetailsDtoAssessmentStatus } from '../model/tracingContactDetailsDtoAssessmentStatus.ts';
import type { PaginatedResponse } from '../pagination/pagination.types.ts';

type SecondParameter<T extends (...args: any) => any> = Parameters<T>[1];

export interface ScreeningTracingIndividualDto {
  id: string;
  firstName: string;
  lastName: string;
  birthDate: string;
  screeningCaseId: string;
  screeningCaseTitle: string;
  assessmentStatus: TracingContactDetailsDtoAssessmentStatus;
  referralDate: string;
  therapyStartDate: string;
  therapyEndDate: string;
  appointments: AppointmentDto[];
  labOrders: LabOrderDto[];
  created: string;
}

export interface CreateScreeningTracingContactDto {
  screeningCaseId: string;
  personId: string;
}

export type ScreeningTracingIndividualListQuery = {
  screeningCaseId: string;
  page?: number;
  size?: number;
  search?: string;
};

export type ScreeningTracingIndividualListResponse = PaginatedResponse<ScreeningTracingIndividualDto>;

export const getScreeningTracingIndividuals = (
  params: ScreeningTracingIndividualListQuery,
  options?: SecondParameter<typeof request>,
  signal?: AbortSignal,
) => {
  return request<ScreeningTracingIndividualListResponse>(
    {
      url: `/api/screening-tracing/individuals`,
      method: 'GET',
      params,
      signal,
    },
    options,
  );
};

export const createScreeningTracingContact = (
  createScreeningTracingContactDto: BodyType<CreateScreeningTracingContactDto>,
  options?: SecondParameter<typeof request>,
  signal?: AbortSignal,
) => {
  return request<string>(
    {
      url: `/api/screening-tracing/individuals`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: createScreeningTracingContactDto,
      signal,
    },
    options,
  );
};

export const getGetScreeningTracingIndividualsQueryKey = (params?: ScreeningTracingIndividualListQuery) => {
  return [`/api/screening-tracing/individuals`, ...(params ? [params] : [])] as const;
};

export const getGetScreeningTracingIndividualsQueryOptions = <
  TData = Awaited<ReturnType<typeof getScreeningTracingIndividuals>>,
  TError = ErrorType<unknown>,
>(
  params: ScreeningTracingIndividualListQuery,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getScreeningTracingIndividuals>>, TError, TData>>;
    request?: SecondParameter<typeof request>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetScreeningTracingIndividualsQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getScreeningTracingIndividuals>>> = ({ signal }) =>
    getScreeningTracingIndividuals(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getScreeningTracingIndividuals>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetScreeningTracingIndividualsQueryResult = NonNullable<
  Awaited<ReturnType<typeof getScreeningTracingIndividuals>>
>;
export type GetScreeningTracingIndividualsQueryError = ErrorType<unknown>;

export function useGetScreeningTracingIndividuals<
  TData = Awaited<ReturnType<typeof getScreeningTracingIndividuals>>,
  TError = ErrorType<unknown>,
>(
  params: ScreeningTracingIndividualListQuery,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getScreeningTracingIndividuals>>, TError, TData>>;
    request?: SecondParameter<typeof request>;
  },
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetScreeningTracingIndividualsQueryOptions(params, options);

  const query = useQuery({ ...queryOptions, placeholderData: keepPreviousData }) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

export const getGetScreeningTracingIndividualsSuspenseQueryOptions = <
  TData = Awaited<ReturnType<typeof getScreeningTracingIndividuals>>,
  TError = ErrorType<unknown>,
>(
  params: ScreeningTracingIndividualListQuery,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<typeof getScreeningTracingIndividuals>>, TError, TData>
    >;
    request?: SecondParameter<typeof request>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetScreeningTracingIndividualsQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getScreeningTracingIndividuals>>> = ({ signal }) =>
    getScreeningTracingIndividuals(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseSuspenseQueryOptions<
    Awaited<ReturnType<typeof getScreeningTracingIndividuals>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetScreeningTracingIndividualsSuspenseQueryResult = NonNullable<
  Awaited<ReturnType<typeof getScreeningTracingIndividuals>>
>;
export type GetScreeningTracingIndividualsSuspenseQueryError = ErrorType<unknown>;

export function useGetScreeningTracingIndividualsSuspense<
  TData = Awaited<ReturnType<typeof getScreeningTracingIndividuals>>,
  TError = ErrorType<unknown>,
>(
  params: ScreeningTracingIndividualListQuery,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<typeof getScreeningTracingIndividuals>>, TError, TData>
    >;
    request?: SecondParameter<typeof request>;
  },
): UseSuspenseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetScreeningTracingIndividualsSuspenseQueryOptions(params, options);

  const query = useSuspenseQuery(queryOptions) as UseSuspenseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

export const getCreateScreeningTracingContactMutationOptions = <
  TData = Awaited<ReturnType<typeof createScreeningTracingContact>>,
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<TData, TError, { data: BodyType<CreateScreeningTracingContactDto> }, TContext>;
  request?: SecondParameter<typeof request>;
}) => {
  const mutationKey = ['createScreeningTracingContact'];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof createScreeningTracingContact>>,
    { data: BodyType<CreateScreeningTracingContactDto> }
  > = (props) => {
    const { data } = props ?? {};

    return createScreeningTracingContact(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions } as UseMutationOptions<
    TData,
    TError,
    { data: BodyType<CreateScreeningTracingContactDto> },
    TContext
  >;
};

export type CreateScreeningTracingContactMutationResult = NonNullable<
  Awaited<ReturnType<typeof createScreeningTracingContact>>
>;
export type CreateScreeningTracingContactMutationBody = BodyType<CreateScreeningTracingContactDto>;
export type CreateScreeningTracingContactMutationError = ErrorType<unknown>;

export const useCreateScreeningTracingContact = <
  TData = Awaited<ReturnType<typeof createScreeningTracingContact>>,
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<TData, TError, { data: BodyType<CreateScreeningTracingContactDto> }, TContext>;
  request?: SecondParameter<typeof request>;
}): UseMutationResult<TData, TError, { data: BodyType<CreateScreeningTracingContactDto> }, TContext> => {
  const mutationOptions = getCreateScreeningTracingContactMutationOptions(options);

  return useMutation(mutationOptions);
};
